import React from "react";

/**
 * DossierAnalytics — the four archival meters that live at the top
 * of every dossier.
 *
 * Rendering intent (V2 art-direction pass):
 *   1. First, a typewriter drew a row of empty boxes — □□□□□□□□□□
 *   2. Then, an investigator physically COLORED IN the filled
 *      squares afterwards with marker/pen. The fills are never
 *      perfectly uniform: opacity, rotation, and offset vary per
 *      square so they read as hand-applied ink, not CSS.
 *
 * No gradients. No glossy UI. Muted archival ink only.
 */

const SEGMENTS = 14;

// Muted ARCHIVAL palette — deep red / faded navy / forest green /
// aged violet. Each meter carries its own ink colour, plus a slightly
// darker tone for the strokes so the "hand-drawn" look reads.
const PALETTE = {
  controversy: { ink: "#8E2A26", dark: "#6a1e1c", text: "#8E2A26" },
  evidence:    { ink: "#28477A", dark: "#1b3260", text: "#28477A" },
  quality:     { ink: "#2E5D34", dark: "#204324", text: "#2E5D34" },
  consensus:   { ink: "#463A6E", dark: "#312953", text: "#463A6E" },
};

// Deterministic per-square variance so no two colored-in boxes look
// identical, without any React state / random at render time.
const _hash = (s) => {
  let h = 2166136261;
  const str = String(s || "");
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 16777619);
  }
  return h >>> 0;
};
const _wobble = (seed, i) => {
  const h = _hash(`${seed}-${i}`);
  return {
    opacity: 0.78 + ((h % 100) / 100) * 0.22,        // 0.78..1.0
    rot:     (((h >> 3) % 100) / 100 - 0.5) * 3,     // ±1.5deg
    tx:      (((h >> 6) % 100) / 100 - 0.5) * 1.5,   // ±0.75px
    ty:      (((h >> 9) % 100) / 100 - 0.5) * 1.5,
  };
};

const Meter = ({ score = 50, kind = "controversy", label, testid, seed }) => {
  const clamped = Math.max(0, Math.min(100, Number(score) || 0));
  const filled = Math.round((clamped / 100) * SEGMENTS);
  const p = PALETTE[kind] || PALETTE.controversy;

  return (
    <div className="dossier-meter" data-testid={testid}>
      <span className="dossier-meter__label">{label}</span>
      <span className="dossier-meter__row" aria-hidden="true">
        {Array.from({ length: SEGMENTS }).map((_, i) => {
          const on = i < filled;
          const w = _wobble(seed || testid, i);
          return (
            <span
              key={i}
              className={`dossier-meter__box ${on ? "on" : ""}`}
              style={on
                ? {
                    background: p.ink,
                    borderColor: p.dark,
                    opacity: w.opacity,
                    transform: `translate(${w.tx}px, ${w.ty}px) rotate(${w.rot}deg)`,
                    boxShadow: `inset 0 0 3px ${p.dark}66`,
                  }
                : undefined}
            />
          );
        })}
      </span>
      <span className="dossier-meter__value" style={{ color: p.text }}>
        {clamped}<span className="dossier-meter__value-total">/100</span>
      </span>
    </div>
  );
};

export const DossierAnalytics = ({ topic }) => {
  const seed = topic?.slug || topic?.id || "meter";
  return (
    <div className="dossier-analytics-grid" data-testid="dossier-analytics">
      <Meter
        kind="controversy"
        score={topic.controversy_score}
        label="// CONTROVERSY"
        testid="meter-controversy"
        seed={`${seed}-c`}
      />
      <Meter
        kind="evidence"
        score={topic.evidence_score ?? 60}
        label="// EVIDENCE"
        testid="meter-evidence"
        seed={`${seed}-e`}
      />
      <Meter
        kind="quality"
        score={topic.source_quality_score ?? 60}
        label="// SOURCE QUALITY"
        testid="meter-quality"
        seed={`${seed}-q`}
      />
      <Meter
        kind="consensus"
        score={topic.consensus_score ?? 50}
        label="// CONSENSUS"
        testid="meter-consensus"
        seed={`${seed}-s`}
      />
    </div>
  );
};

export default DossierAnalytics;
